import type { UnitDef, TierDef } from './types';

// ─── Tier Definitions ───────────────────────────────────────────────────────────

export const TIER_DEFS: TierDef[] = [
  {
    id: 1,
    name: 'Planetary Colonization',
    theme: 'planet',
    resources: { primary: 'Matter', knowledge: 'Knowledge', energy: 'Energy', pvp: 'Influence' },
    unlockRequirement: { type: 'unit_owned', unitId: 14, amount: 1 },
    prestige: 1,
  },
  {
    id: 2,
    name: 'Solar Expansion',
    theme: 'solar',
    resources: { primary: 'Alloys', knowledge: 'Astrophysics', energy: 'Stellar Energy', pvp: 'Fleet Command' },
    unlockRequirement: { type: 'lifetime_resource', resourceKey: 'primary', amount: 1e12 },
    prestige: 1.25,
  },
];

// ─── Tier 1 Units ───────────────────────────────────────────────────────────────
// Theme: Planetary colonization — seed systems awakening creators

export const TIER1_UNITS: UnitDef[] = [
  // ── Production Chain (1-10) ──────────────────────────────────────────────────
  {
    id: 1, name: 'Tech Drones', role: 'producer', chain: 'production',
    baseCost: 10, costScale: 1.15, baseOutput: 1.0,
  },
  {
    id: 2, name: 'Gather Drones', role: 'builder', chain: 'production',
    baseCost: 100, costScale: 1.15, builderOutput: 0.2, builderTarget: 1,
  },
  {
    id: 3, name: 'Extraction Rigs', role: 'builder', chain: 'production',
    baseCost: 500, costScale: 1.15, builderOutput: 0.1, builderTarget: 2,
  },
  {
    id: 4, name: 'Resource Depots', role: 'amplifier', chain: 'production',
    baseCost: 1000, costScale: 1.22,
    ampFormula: 'linear', ampTargets: 'production', bonusPerUnit: 0.02,
  },
  {
    id: 5, name: 'Fabrication Pods', role: 'builder', chain: 'production',
    baseCost: 5000, costScale: 1.16, builderOutput: 0.05, builderTarget: 3,
  },
  {
    id: 6, name: 'Assembly Lines', role: 'amplifier', chain: 'production',
    baseCost: 2e4, costScale: 1.24,
    ampFormula: 'linear', ampTargets: 'production', bonusPerUnit: 0.03,
  },
  {
    id: 7, name: 'Industrial Nodes', role: 'builder', chain: 'production',
    baseCost: 1e5, costScale: 1.17, builderOutput: 0.025, builderTarget: 5,
  },
  {
    id: 8, name: 'Processing Plants', role: 'amplifier', chain: 'production',
    baseCost: 5e5, costScale: 1.27,
    ampFormula: 'log', ampTargets: 'production',
    logFactor: 0.05, logThresholdKey: 'primary',
  },
  {
    id: 9, name: 'Hyper Refineries', role: 'builder', chain: 'production',
    baseCost: 1e6, costScale: 1.18, builderOutput: 0.0125, builderTarget: 7,
  },
  {
    id: 10, name: 'Planetary Factories', role: 'amplifier', chain: 'production',
    baseCost: 1e7, costScale: 1.35,
    ampFormula: 'exponential', ampTargets: 'global', ampBase: 1.25,
  },

  // ── Creation Chain (11-14) ───────────────────────────────────────────────────
  {
    id: 11, name: 'Awakening Chambers', role: 'builder', chain: 'creation',
    baseCost: 1e8, costScale: 1.22, builderOutput: 0.2, builderTarget: 1,
    // Counts as special builder that boosts creators — treated as secondary chain multiplier
  },
  {
    id: 12, name: 'Bio-Constructors', role: 'builder', chain: 'creation',
    baseCost: 5e8, costScale: 1.24, builderOutput: 0.1, builderTarget: 11,
  },
  {
    id: 13, name: 'Creator Pods', role: 'amplifier', chain: 'creation',
    baseCost: 1e9, costScale: 1.28,
    ampFormula: 'linear', ampTargets: 'global', bonusPerUnit: 0.04,
  },
  {
    id: 14, name: 'Creator Enclaves', role: 'amplifier', chain: 'creation',
    baseCost: 1e10, costScale: 1.35,
    ampFormula: 'linear', ampTargets: 'global', bonusPerUnit: 0.05,
  },

  // ── Knowledge Chain (15-18) ──────────────────────────────────────────────────
  {
    id: 15, name: 'Data Nodes', role: 'producer', chain: 'knowledge',
    baseCost: 1e3, costScale: 1.14, baseOutput: 1.0,
  },
  {
    id: 16, name: 'Analysis Cores', role: 'amplifier', chain: 'knowledge',
    baseCost: 1e4, costScale: 1.22,
    ampFormula: 'linear', ampTargets: 'production', bonusPerUnit: 0.02,
  },
  {
    id: 17, name: 'Research Arrays', role: 'builder', chain: 'knowledge',
    baseCost: 1e5, costScale: 1.16, builderOutput: 0.1, builderTarget: 15,
  },
  {
    id: 18, name: 'Planetary AI', role: 'amplifier', chain: 'knowledge',
    baseCost: 1e7, costScale: 1.30,
    ampFormula: 'log', ampTargets: 'global',
    logFactor: 0.10, logThresholdKey: 'knowledge',
  },

  // ── Energy Chain (19-22) ─────────────────────────────────────────────────────
  {
    id: 19, name: 'Solar Collectors', role: 'producer', chain: 'energy',
    baseCost: 1e3, costScale: 1.14, baseOutput: 1.0,
  },
  {
    id: 20, name: 'Energy Grids', role: 'amplifier', chain: 'energy',
    baseCost: 1e4, costScale: 1.22,
    ampFormula: 'linear', ampTargets: 'production', bonusPerUnit: 0.02,
  },
  {
    id: 21, name: 'Fusion Reactors', role: 'builder', chain: 'energy',
    baseCost: 5e4, costScale: 1.18, builderOutput: 0.15, builderTarget: 19,
  },
  {
    id: 22, name: 'Core Taps', role: 'amplifier', chain: 'energy',
    baseCost: 1e6, costScale: 1.30,
    ampFormula: 'exponential', ampTargets: 'global', ampBase: 1.15,
  },

  // ── Warfare / Influence (23-25) ──────────────────────────────────────────────
  {
    id: 23, name: 'Recon Drones', role: 'producer', chain: 'warfare',
    baseCost: 5e3, costScale: 1.15, baseOutput: 1.0,
  },
  {
    id: 24, name: 'Defense Platforms', role: 'amplifier', chain: 'warfare',
    baseCost: 5e4, costScale: 1.22,
    ampFormula: 'linear', ampTargets: 'self', defensePerUnit: 10,
  },
  {
    id: 25, name: 'Command Nexus', role: 'amplifier', chain: 'warfare',
    baseCost: 5e5, costScale: 1.28,
    ampFormula: 'linear', ampTargets: 'self',
    attackPerUnit: 12, prodBonusPerUnit: 0.01,
  },
];

// ─── Tier 2 Units ───────────────────────────────────────────────────────────────
// Theme: Solar system expansion — creators dominate star systems

export const TIER2_UNITS: UnitDef[] = [
  // ── Production Chain (1-10) ──────────────────────────────────────────────────
  {
    id: 1, name: 'Mining Drones', role: 'producer', chain: 'production',
    baseCost: 1e10, costScale: 1.15, baseOutput: 1.0,
  },
  {
    id: 2, name: 'Asteroid Extractors', role: 'builder', chain: 'production',
    baseCost: 1e11, costScale: 1.15, builderOutput: 0.2, builderTarget: 1,
  },
  {
    id: 3, name: 'Orbital Foundries', role: 'builder', chain: 'production',
    baseCost: 5e11, costScale: 1.15, builderOutput: 0.1, builderTarget: 2,
  },
  {
    id: 4, name: 'Station Clusters', role: 'amplifier', chain: 'production',
    baseCost: 1e12, costScale: 1.22,
    ampFormula: 'linear', ampTargets: 'production', bonusPerUnit: 0.02,
  },
  {
    id: 5, name: 'Planet Crackers', role: 'builder', chain: 'production',
    baseCost: 5e12, costScale: 1.16, builderOutput: 0.05, builderTarget: 3,
  },
  {
    id: 6, name: 'Gas Harvesters', role: 'amplifier', chain: 'production',
    baseCost: 2e13, costScale: 1.24,
    ampFormula: 'linear', ampTargets: 'production', bonusPerUnit: 0.03,
  },
  {
    id: 7, name: 'Ringworld Segments', role: 'builder', chain: 'production',
    baseCost: 1e14, costScale: 1.17, builderOutput: 0.025, builderTarget: 5,
  },
  {
    id: 8, name: 'Stellar Refineries', role: 'amplifier', chain: 'production',
    baseCost: 5e14, costScale: 1.27,
    ampFormula: 'log', ampTargets: 'production',
    logFactor: 0.05, logThresholdKey: 'primary',
  },
  {
    id: 9, name: 'Solar Fabricators', role: 'builder', chain: 'production',
    baseCost: 1e15, costScale: 1.18, builderOutput: 0.0125, builderTarget: 7,
  },
  {
    id: 10, name: 'System Megafactories', role: 'amplifier', chain: 'production',
    baseCost: 1e16, costScale: 1.35,
    ampFormula: 'exponential', ampTargets: 'global', ampBase: 1.25,
  },

  // ── Creation Chain (11-14) ───────────────────────────────────────────────────
  {
    id: 11, name: 'Creator Ships', role: 'builder', chain: 'creation',
    baseCost: 1e17, costScale: 1.22, builderOutput: 0.2, builderTarget: 1,
  },
  {
    id: 12, name: 'Expansion Arks', role: 'builder', chain: 'creation',
    baseCost: 5e17, costScale: 1.24, builderOutput: 0.1, builderTarget: 11,
  },
  {
    id: 13, name: 'Stellar Architects', role: 'amplifier', chain: 'creation',
    baseCost: 1e18, costScale: 1.28,
    ampFormula: 'linear', ampTargets: 'global', bonusPerUnit: 0.04,
  },
  {
    id: 14, name: 'System Overseers', role: 'amplifier', chain: 'creation',
    baseCost: 1e19, costScale: 1.35,
    ampFormula: 'linear', ampTargets: 'global', bonusPerUnit: 0.05,
  },

  // ── Knowledge Chain (15-18) ──────────────────────────────────────────────────
  {
    id: 15, name: 'Deep Space Sensors', role: 'producer', chain: 'knowledge',
    baseCost: 1e12, costScale: 1.14, baseOutput: 1.0,
  },
  {
    id: 16, name: 'Stellar Think Tanks', role: 'amplifier', chain: 'knowledge',
    baseCost: 1e13, costScale: 1.22,
    ampFormula: 'linear', ampTargets: 'production', bonusPerUnit: 0.02,
  },
  {
    id: 17, name: 'Quantum Labs', role: 'builder', chain: 'knowledge',
    baseCost: 1e14, costScale: 1.16, builderOutput: 0.1, builderTarget: 15,
  },
  {
    id: 18, name: 'System AI Core', role: 'amplifier', chain: 'knowledge',
    baseCost: 1e16, costScale: 1.30,
    ampFormula: 'log', ampTargets: 'global',
    logFactor: 0.10, logThresholdKey: 'knowledge',
  },

  // ── Energy Chain (19-22) ─────────────────────────────────────────────────────
  {
    id: 19, name: 'Solar Arrays', role: 'producer', chain: 'energy',
    baseCost: 1e12, costScale: 1.14, baseOutput: 1.0,
  },
  {
    id: 20, name: 'Stellar Siphons', role: 'amplifier', chain: 'energy',
    baseCost: 1e13, costScale: 1.22,
    ampFormula: 'linear', ampTargets: 'production', bonusPerUnit: 0.02,
  },
  {
    id: 21, name: 'Fusion Stars', role: 'builder', chain: 'energy',
    baseCost: 5e13, costScale: 1.18, builderOutput: 0.15, builderTarget: 19,
  },
  {
    id: 22, name: 'Proto Dyson Rings', role: 'amplifier', chain: 'energy',
    baseCost: 1e15, costScale: 1.30,
    ampFormula: 'exponential', ampTargets: 'global', ampBase: 1.15,
  },

  // ── Warfare / Fleet Command (23-25) ─────────────────────────────────────────
  {
    id: 23, name: 'Space Rangers', role: 'producer', chain: 'warfare',
    baseCost: 5e12, costScale: 1.15, baseOutput: 1.0,
  },
  {
    id: 24, name: 'Fleet Warships', role: 'amplifier', chain: 'warfare',
    baseCost: 5e13, costScale: 1.22,
    ampFormula: 'linear', ampTargets: 'self', defensePerUnit: 15,
  },
  {
    id: 25, name: 'Solar Command', role: 'amplifier', chain: 'warfare',
    baseCost: 5e14, costScale: 1.28,
    ampFormula: 'linear', ampTargets: 'self',
    attackPerUnit: 18, prodBonusPerUnit: 0.01,
  },
];

// ─── Lookup helpers ─────────────────────────────────────────────────────────────

const UNIT_MAP: Record<number, UnitDef[]> = {
  1: TIER1_UNITS,
  2: TIER2_UNITS,
};

export function getUnitsForTier(tier: number): UnitDef[] {
  return UNIT_MAP[tier] ?? [];
}

export function getUnitDef(tier: number, unitId: number): UnitDef | undefined {
  return UNIT_MAP[tier]?.find((u) => u.id === unitId);
}

export function getTierDef(tier: number): TierDef | undefined {
  return TIER_DEFS.find((t) => t.id === tier);
}
